/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { useRef, useState } from 'react';

import { GuideProgram } from 'client-code/filtered-guide';

import { InteractiveComponents } from '../../../../core/enums';
import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../core/tivo/tivo-redux-wrapper';
import { onSetFilteredGuideTurboScrollAction } from '../../../../store/guide/filteredguide/filteredguide.actions';
import { resetFilteredGuideIndexes } from '../../../../store/guide/filteredguide/filteredguide.reducers';
import { useUpdateEffect } from '../../../hooks';
import {
    getLastCompletedHour,
    getNextTurboScrollTimeVallue,
    GuidePaginationState,
    LimitedGuideProgramResult,
} from '../FilteredGuideTable/filteredguidetable.utils';

type HookProps = {
    category: string;
    channelsType: string;
    guideProgramList: LimitedGuideProgramResult[];
    minStartTimestamp: number;
    maxStartTimestamp: number;
    startTimestamp: Date;
    cacheRequestToken: string | undefined;
    onFetchInitialFilteredGuideContent: (
        category: string,
        channelsType: string,
        skipCurrentTimeCheck?: boolean,
        cacheToken?: string,
    ) => void;
    onSetPageState: (newState: GuidePaginationState) => void;
    onSetStartTimestamp: (timestamp: Date) => void;
};

export const useTurboScroll = ({
    category,
    channelsType,
    guideProgramList,
    minStartTimestamp,
    maxStartTimestamp,
    startTimestamp,
    cacheRequestToken,
    onFetchInitialFilteredGuideContent,
    onSetStartTimestamp,
    onSetPageState,
}: HookProps) => {
    const { verticalPosition, filteredGuideTurboScroll, filteredGuideTurboScrollDirection } =
        useTivoSelector((state: RootState) => state.filteredGuide);
    const [turboScrollCurrentDisplayedDate, setTurboScrollCurrentDisplayedDate] =
        useState<Date>(getLastCompletedHour());

    const wasTurboScrollEnabled = useRef(false);

    const dispatch = useTivoDispatch();

    const resetGuideForTurboScroll = () => {
        onSetPageState({
            currentPageIndex: 0,
            previousPageIndex: 0,
            pages: new Map(),
        });
        dispatch(resetFilteredGuideIndexes());
    };

    useUpdateEffect(() => {
        if (filteredGuideTurboScroll) {
            wasTurboScrollEnabled.current = true;
            const selectedProgram = guideProgramList.filter(
                (item): item is { type: 'program'; value: GuideProgram } => item.type === 'program',
            )?.[verticalPosition];
            const date = selectedProgram?.value?.startTime
                ? new Date(selectedProgram.value.startTime * 1000)
                : startTimestamp;
            setTurboScrollCurrentDisplayedDate(date);
            resetGuideForTurboScroll();
        } else if (!filteredGuideTurboScroll && wasTurboScrollEnabled.current) {
            wasTurboScrollEnabled.current = false;
            onSetStartTimestamp(turboScrollCurrentDisplayedDate);
            onFetchInitialFilteredGuideContent(category, channelsType, true, cacheRequestToken);
        }
    }, [filteredGuideTurboScroll]);

    useUpdateEffect(() => {
        if (guideProgramList.length !== 0 && filteredGuideTurboScroll) {
            resetGuideForTurboScroll();
        }
    }, [guideProgramList]);

    useUpdateEffect(() => {
        let intervalId: null | NodeJS.Timeout = null;

        intervalId = setInterval(() => {
            if (filteredGuideTurboScroll) {
                setTurboScrollCurrentDisplayedDate((prevDate) => {
                    const nextDate = getNextTurboScrollTimeVallue(
                        prevDate,
                        filteredGuideTurboScrollDirection,
                        minStartTimestamp,
                        maxStartTimestamp,
                    );

                    return nextDate;
                });
            }
        }, 250);

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [filteredGuideTurboScroll, filteredGuideTurboScrollDirection]);

    useUpdateEffect(() => {
        if (
            (turboScrollCurrentDisplayedDate.getTime() === minStartTimestamp * 1000 &&
                filteredGuideTurboScrollDirection === InteractiveComponents.NORTH) ||
            (turboScrollCurrentDisplayedDate.getTime() === maxStartTimestamp * 1000 &&
                filteredGuideTurboScrollDirection === InteractiveComponents.SOUTH)
        ) {
            dispatch(onSetFilteredGuideTurboScrollAction(false, InteractiveComponents.NORTH));
        }
    }, [turboScrollCurrentDisplayedDate]);

    return { turboScrollCurrentDisplayedDate, filteredGuideTurboScroll };
};
